'use client';

import Link from 'next/link';
import type { AnchorHTMLAttributes, MouseEventHandler, ReactNode } from 'react';

import type { TBlogCtaAnalyticsName } from '@/app/_lib/analytics-events';
import { trackBlogCtaClick } from '@/app/_lib/analytics-events';
import { trackBlogCtaClick as trackBlogCtaLocationClick, type TBlogCtaLocation } from '@/app/_utils/analytics';

const externalTabProps = (href: string) =>
  href.startsWith('http') ? ({ target: '_blank' as const, rel: 'noopener noreferrer' as const }) : {};

interface IBlogTrackedCtaLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string;
  pageSlug: string;
  ctaName: TBlogCtaAnalyticsName;
  section?: string;
  location?: TBlogCtaLocation;
  children: ReactNode;
}

/**
 * 블로그 전역 등 — 클릭 시 `blog_cta_click` 후 네비게이션.
 * mailto는 GA 후 `location.assign`으로 처리(환경 따라 기본 mailto 무응 완화).
 */
const BlogTrackedCtaLink = ({
  href,
  pageSlug,
  ctaName,
  section = 'bottom_cta',
  location,
  children,
  onClick,
  ...rest
}: IBlogTrackedCtaLinkProps) => {
  const sendAnalytics = () => {
    trackBlogCtaClick({ cta_name: ctaName, page: pageSlug, section });
    if (location) {
      trackBlogCtaLocationClick({
        location,
        page: pageSlug,
      });
    }
  };

  const handleClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    sendAnalytics();
    onClick?.(e);
  };

  const restWithoutOnClick = rest;

  if (href.startsWith('mailto:')) {
    const handleMailto: MouseEventHandler<HTMLAnchorElement> = (e) => {
      sendAnalytics();
      e.preventDefault();
      window.location.assign(href);
      onClick?.(e);
    };

    return (
      <a href={href} {...restWithoutOnClick} onClick={handleMailto}>
        {children}
      </a>
    );
  }

  if (href.startsWith('http')) {
    return (
      <a href={href} {...externalTabProps(href)} {...restWithoutOnClick} onClick={handleClick}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} {...restWithoutOnClick} onClick={handleClick}>
      {children}
    </Link>
  );
};

export default BlogTrackedCtaLink;
